/*
 *  Copyright (c) 2014 The WebRTC project authors. All Rights Reserved.
 *
 *  Use of this source code is governed by a BSD-style license
 *  that can be found in the LICENSE file in the root of the source
 *  tree.
 */

/*
 * In generic cameras using Chrome rescaler, all resolutions should be supported
 * up to a given one and none beyond there. Special cameras, such as digitizers,
 * might support only one resolution.
 */

/*
 * "Analyze performance for "resolution"" test uses getStats, canvas and the
 * video element to analyze the video frames from a capture device. It will
 * report number of black frames, frozen frames, tested frames and various stats
 * like average encode time and FPS. A test case will be created per mandatory
 * resolution found in the "resolutions" array.
 */

/*addTest(testSuiteName.CAMERA, testCaseName.CHECKRESOLUTION240, function(test) {
  var camResolutionsTest = new CamResolutionsTest(test , [[320, 240]]);
  camResolutionsTest.run();
});

addTest(testSuiteName.CAMERA, testCaseName.CHECKRESOLUTION480, function(test) {
  var camResolutionsTest = new CamResolutionsTest(test, [[640, 480]]);
  camResolutionsTest.run();
});

addTest(testSuiteName.CAMERA, testCaseName.CHECKRESOLUTION720, function(test) {
  var camResolutionsTest = new CamResolutionsTest(test, [[1280, 720]]);
  camResolutionsTest.run();
});

addTest(testSuiteName.CAMERA,
    testCaseName.CHECKSUPPORTEDRESOLUTIONS, function(test) {
      var resolutionArray = [
        [160, 120], [320, 180], [320, 240], [640, 360], [640, 480], [768, 576],
        [1024, 576], [1280, 720], [1280, 768], [1280, 800], [1920, 1080],
        [1920, 1200], [3840, 2160], [4096, 2160]
      ];
      var camResolutionsTest = new CamResolutionsTest(test, resolutionArray);
      camResolutionsTest.run();
    });*/

import VideoFrameChecker from "./videoframechecker";
import Call from "./call";
import * as Utils from "./util";

export default function CamResolutionsTest(resolutions) {
  this.resolutions = resolutions;
  this.currentResolution = 0;
  this.isMuted = false;
  this.isShuttingDown = false;
}

CamResolutionsTest.prototype = {
  run: function(test, sourceId) {
    this.test = test;
    this.sourceId = sourceId;
    this.startGetUserMedia(this.resolutions[this.currentResolution]);
  },

  startGetUserMedia: function(resolution) {
    var constraints = {
      audio: false,
      video: {
        width: {exact: resolution[0]},
        height: {exact: resolution[1]}
      }
    };
    navigator.mediaDevices.getUserMedia(constraints)
        .then(function(stream) {
          // Do not check actual video frames when more than one resolution is
          // provided.
          if (this.resolutions.length > 1) {
            this.test.reportSuccess('Supported: ' + resolution[0] + 'x' + resolution[1]);
            stream.getTracks().forEach(function(track) {
              track.stop();
            });
            this.maybeContinueGetUserMedia();
          } else {
            this.collectAndAnalyzeStats_(stream, resolution);
          }
        }.bind(this))
        .catch(function(error) {
          console.log(error);
          if (this.resolutions.length > 1) {
            this.test.reportInfo(resolution[0] + 'x' + resolution[1] + ' not supported');
          } else {
            this.test.reportError('getUserMedia failed with error: ' + error.name);
          }
          this.maybeContinueGetUserMedia();
        }.bind(this));
  },

  maybeContinueGetUserMedia: function() {
    if (this.currentResolution === this.resolutions.length) {
      this.test.done();
      return;
    }
    this.startGetUserMedia(this.resolutions[this.currentResolution++]);
  },

  collectAndAnalyzeStats_: function(stream, resolution) {
    var tracks = stream.getVideoTracks();
    if (tracks.length < 1) {
      this.test.reportError('No video track in returned stream.');
      this.maybeContinueGetUserMedia();
      return;
    }

    // Firefox does not support event handlers on mediaStreamTrack yet.
    // https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack
    // TODO: remove if (...) when event handlers are supported by Firefox.
    var videoTrack = tracks[0];
    if (typeof videoTrack.addEventListener === 'function') {
      // Register events.
      videoTrack.addEventListener('ended', function() {
        // Ignore events when shutting down the test.
        if (this.isShuttingDown) {
          return;
        }
        this.test.reportError('Video track ended, camera stopped working');
      }.bind(this));
      videoTrack.addEventListener('mute', function() {
        // Ignore events when shutting down the test.
        if (this.isShuttingDown) {
          return;
        }
        this.test.reportWarning('Your camera reported itself as muted.');
        // MediaStreamTrack.muted property is not wired up in Chrome yet,
        // checking isMuted local state.
        this.isMuted = true;
      }.bind(this));
      videoTrack.addEventListener('unmute', function() {
        // Ignore events when shutting down the test.
        if (this.isShuttingDown) {
          return;
        }
        this.test.reportInfo('Your camera reported itself as unmuted.');
        this.isMuted = false;
      }.bind(this));
    }

    var video = document.createElement('video');
    video.setAttribute('autoplay', '');
    video.setAttribute('muted', '');
    video.width = resolution[0];
    video.height = resolution[1];
    video.srcObject = stream;
    var frameChecker = new VideoFrameChecker(video);
    var call = new Call(null, this.test);
    call.pc1.addStream(stream);
    call.establishConnection();
    call.gatherStats(call.pc1, null, stream,
        this.onCallEnded_.bind(this, resolution, video,
            stream, frameChecker),
        100);

    this.test.setTimeoutWithProgressBar(this.endCall_.bind(this, call, stream), 8000);
  },

  onCallEnded_: function(resolution, videoElement, stream, frameChecker,
                         stats, statsTime) {
    this.analyzeStats_(resolution, videoElement, stream, frameChecker,
        stats, statsTime);

    frameChecker.stop();

    this.test.done();
  },

  analyzeStats_: function(resolution, videoElement, stream,
                          frameChecker, stats, statsTime) {
    var googAvgEncodeTime = [];
    var googAvgFrameRateInput = [];
    var googAvgFrameRateSent = [];
    var statsReport = {};
    var frameStats = frameChecker.frameStats;

    for (var index in stats) {
      if (stats[index].type === 'ssrc') {
        // Make sure to only capture stats after the encoder is setup.
        if (parseInt(stats[index].googFrameRateInput) > 0) {
          googAvgEncodeTime.push(
              parseInt(stats[index].googAvgEncodeMs));
          googAvgFrameRateInput.push(
              parseInt(stats[index].googFrameRateInput));
          googAvgFrameRateSent.push(
              parseInt(stats[index].googFrameRateSent));
        }
      }
    }

    statsReport.cameraName = stream.getVideoTracks()[0].label || NaN;
    statsReport.actualVideoWidth = videoElement.videoWidth;
    statsReport.actualVideoHeight = videoElement.videoHeight;
    statsReport.mandatoryWidth = resolution[0];
    statsReport.mandatoryHeight = resolution[1];
    statsReport.encodeSetupTimeMs =
        this.extractEncoderSetupTime_(stats, statsTime);
    statsReport.avgEncodeTimeMs = Utils.arrayAverage(googAvgEncodeTime);
    statsReport.minEncodeTimeMs = Utils.arrayMin(googAvgEncodeTime);
    statsReport.maxEncodeTimeMs = Utils.arrayMax(googAvgEncodeTime);
    statsReport.avgInputFps = Utils.arrayAverage(googAvgFrameRateInput);
    statsReport.minInputFps = Utils.arrayMin(googAvgFrameRateInput);
    statsReport.maxInputFps = Utils.arrayMax(googAvgFrameRateInput);
    statsReport.avgSentFps = Utils.arrayAverage(googAvgFrameRateSent);
    statsReport.minSentFps = Utils.arrayMin(googAvgFrameRateSent);
    statsReport.maxSentFps = Utils.arrayMax(googAvgFrameRateSent);
    statsReport.isMuted = this.isMuted;
    statsReport.testedFrames = frameStats.numFrames;
    statsReport.blackFrames = frameStats.numBlackFrames;
    statsReport.frozenFrames = frameStats.numFrozenFrames;

    // TODO: Add a reportInfo() function with a table format to display
    // values clearer.
    //report.traceEventInstant('video-stats', statsReport);
    console.log(statsReport);
    this.testExpectations_(statsReport);
  },

  endCall_: function(callObject, stream) {
    this.isShuttingDown = true;
    stream.getTracks().forEach(function(track) {
      track.stop();
    });
    callObject.close();
  },

  extractEncoderSetupTime_: function(stats, statsTime) {
    for (var index = 0; index !== stats.length; index++) {
      if (stats[index].type === 'ssrc') {
        if (parseInt(stats[index].googFrameRateInput) > 0) {
          return JSON.stringify(statsTime[index] - statsTime[0]);
        }
      }
    }
    return NaN;
  },

  resolutionMatchesIndependentOfRotationOrCrop_: function(aWidth, aHeight,
                                                          bWidth, bHeight) {
    var minRes = Math.min(bWidth, bHeight);
    return (aWidth === bWidth && aHeight === bHeight) ||
        (aWidth === bHeight && aHeight === bWidth) ||
        (aWidth === minRes && bHeight === minRes);
  },

  testExpectations_: function(info) {
    var notAvailableStats = [];
    for (var key in info) {
      if (info.hasOwnProperty(key)) {
        if (typeof info[key] === 'number' && isNaN(info[key])) {
          notAvailableStats.push(key);
        } else {
          this.test.reportInfo(key + ': ' + info[key]);
        }
      }
    }
    if (notAvailableStats.length !== 0) {
      this.test.reportInfo('Not available: ' + notAvailableStats.join(', '));
    }

    if (isNaN(info.avgSentFps)) {
      this.test.reportInfo('Cannot verify sent FPS.');
    } else if (info.avgSentFps < 5) {
      this.test.reportError('Low average sent FPS: ' + info.avgSentFps);
    } else {
      this.test.reportSuccess('Average FPS above threshold');
    }
    if (!this.resolutionMatchesIndependentOfRotationOrCrop_(
        info.actualVideoWidth, info.actualVideoHeight, info.mandatoryWidth,
        info.mandatoryHeight)) {
      this.test.reportError('Incorrect captured resolution.');
    } else {
      this.test.reportSuccess('Captured video using expected resolution.');
    }
    if (info.testedFrames === 0) {
      this.test.reportError('Could not analyze any video frame.');
    } else {
      if (info.blackFrames > info.testedFrames / 3) {
        this.test.reportError('Camera delivering lots of black frames.');
      }
      if (info.frozenFrames > info.testedFrames / 3) {
        this.test.reportError('Camera delivering lots of frozen frames.');
      }
    }
  }
};
